import {
  IProjectCollateralType,
  IProjectCreditorPosition,
} from '~/@api/generated-api-types-project/Api'
import { nonNullable } from '~/@lib/utils/type.utils'
import {
  PROJECT_COLLATERAL_TYPE_OTHER,
  type ProjectSchema,
} from '~/@pages_investing/projects/_project_types/project.types'
import { Text } from '~/@mid_ui/typography/Text'
import { getProjectCollateralTypeLabel } from '~/@pages_investing/projects/_projects_ui/project-collateral/labelByCollateralType'
import { DetailHeading } from '../detail-heading/DetailHeading'
import { DetailItem, type DetailItemRow } from '../detail-item/DetailItem'
import { customTextRow } from '../detail-html-value/customTextRow'
import { hasCustomText } from '../detail-html-value/hasCustomText'
import { projectDetailTooltipByType } from '../detail-tooltips/detailTooltips'

type Props = { project: ProjectSchema }

function InvestmentSecurity({ project }: Props) {
  const {
    collateralType,
    collateralTypeCustom,
    otherCollaterals,
    coInvestment,
    ltlPercentage,
    ltlPercentageCustom,
    creditorPosition,
    creditorPositionCustom,
    collateralAgent,
    latePaymentFeePercentage,
  } = project.collateralInfo ?? {}

  // Kolaterál: enum label (Nehnuteľnosť), pri „Iné" vlastný HTML text; „Iné" bez textu = N/A
  const collateralRow = (() => {
    const heading = (
      <DetailHeading
        title="Kolaterál:"
        tooltip={projectDetailTooltipByType.collateral}
      />
    )
    if (collateralType === PROJECT_COLLATERAL_TYPE_OTHER) {
      return hasCustomText(collateralTypeCustom)
        ? customTextRow({
          title: 'Kolaterál:',
          tooltip: projectDetailTooltipByType.collateral,
          html: collateralTypeCustom,
        })
        : { heading, value: <Text>N/A</Text> }
    }
    return {
      heading,
      value: (
        <Text>
          {getProjectCollateralTypeLabel(
            collateralType ?? IProjectCollateralType.Property
          )}
        </Text>
      ),
    }
  })()

  const items = [
    collateralRow,
    {
      heading: (
        <DetailHeading
          title="Iné zabezpečenie:"
          tooltip={projectDetailTooltipByType.otherCollaterals}
        />
      ),
      value: <Text>{otherCollaterals || 'N/A'}</Text>,
    },
    hasCustomText(ltlPercentageCustom)
      ? customTextRow({
        title: 'Pomer dlhu voči tržnej cene (LTL):',
        tooltip: projectDetailTooltipByType.ltlPercentage,
        html: ltlPercentageCustom,
      })
      : nonNullable(ltlPercentage)
        ? {
          heading: (
            <DetailHeading
              title="Pomer dlhu voči tržnej cene (LTL):"
              tooltip={projectDetailTooltipByType.ltlPercentage}
            />
          ),
          value: <Text>{ltlPercentage}%</Text>,
        }
        : null,
    {
      heading: (
        <DetailHeading
          title="Pozícia veriteľa:"
          tooltip={projectDetailTooltipByType.creditorPosition}
        />
      ),
      // capitalize len pre vlastný text z BO, enum labely majú veľkosť písmen vo svojom texte
      value: (
        <Text
          className={
            creditorPosition === IProjectCreditorPosition.Custom
              ? 'capitalize'
              : undefined
          }
        >
          {getCreditorPositionLabel(creditorPosition, creditorPositionCustom)}
        </Text>
      ),
    },
    nonNullable(collateralAgent)
      ? {
        heading: (
          <DetailHeading
            title="Zabezpečovací agent:"
            tooltip={projectDetailTooltipByType.collateralAgent}
          />
        ),
        value: <Text>{collateralAgent}</Text>,
      }
      : null,
    nonNullable(coInvestment) && collateralAgent
      ? {
        heading: (
          <DetailHeading
            title="Spoluúčasť na investícii:"
            tooltip={projectDetailTooltipByType.coInvestment}
          />
        ),
        value: <Text>{collateralAgent}</Text>,
      }
      : null,
    nonNullable(latePaymentFeePercentage)
      ? {
        heading: (
          <DetailHeading
            title="Úroky z omeškania:"
            tooltip={projectDetailTooltipByType.latePaymentFeePercentage}
          />
        ),
        value: <Text>{latePaymentFeePercentage}% p.a.</Text>,
      }
      : null,
  ]

  return (
    <DetailItem
      sectionId="investmentSecurity"
      title="Zabezpečenie investície"
      rows={items.filter(nonNullable) as Array<DetailItemRow>}
    />
  )
}

const creditorPositionLabelByType: Record<
  Exclude<IProjectCreditorPosition, IProjectCreditorPosition.Custom>,
  string
> = {
  [IProjectCreditorPosition.Junior]: 'Junior (2. poradie)',
  [IProjectCreditorPosition.Senior]: 'Senior',
  [IProjectCreditorPosition.Combined]: 'Combined',
}

function getCreditorPositionLabel(
  creditorPosition: IProjectCreditorPosition,
  creditorPositionCustom: string
) {
  const seniorLabel = creditorPositionLabelByType[IProjectCreditorPosition.Senior]
  if (!creditorPosition) return seniorLabel
  if (creditorPosition === IProjectCreditorPosition.Custom)
    return creditorPositionCustom ?? seniorLabel
  return creditorPositionLabelByType[creditorPosition]
}

export { InvestmentSecurity }
